// Import 3rd-party dependencies
import { ChangeDetectionStrategy, Component, Input, OnInit, OnDestroy } from '@angular/core';
import { combineLatest, Observable } from 'rxjs';
import WorldCities from 'worldcities';
import { debounceTime, distinctUntilChanged, map, share, shareReplay, startWith, switchMap, tap } from 'rxjs/operators';
import { SubSink } from 'subsink';

// Import project-specific dependencies
import { IArticle, SearchDataService } from '@app/services/searchdata/searchdata.service';
import { PeopleService } from '@app/services/people/people.service';
import { TRENDING_HRCONNECT_COMPONENT_CONSTS } from '@app/config/constants';
import { FormControl } from '@angular/forms';

@Component({
  selector: 'app-trending-hrconnect',
  templateUrl: './trending-hrconnect.component.html',
  styleUrls: ['./trending-hrconnect.component.css'],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class TrendingHrConnectComponent implements OnInit, OnDestroy {
  private subs: SubSink;
  consts = TRENDING_HRCONNECT_COMPONENT_CONSTS;

  @Input() countryList: string[];

  defaultCountry = '';

  sortOptions = [this.consts.LABEL_SORT_MOST, this.consts.LABEL_SORT_LATEST, this.consts.LABEL_SORT_NEW];
  ratingOptions = [];
  kbOptions = [];
  categoryOptions = [];

  isLoading = false;
  filterDisabled = false;

  sortControl = new FormControl(this.consts.LABEL_SORT_MOST);
  countryControl = new FormControl();
  ratingControl = new FormControl();
  kbControl = new FormControl();
  categoryControl = new FormControl();

  // Get user's country and use is as default value
  userCountry$ = this.peopleService.currentUser$.pipe(
    map((user) => this.checkCountryCode(user.usageLocation)),
    tap((value) => this.countryControl.setValue(value)),
    tap((value) => (this.defaultCountry = value)),
    shareReplay()
  );

  trendingArticlesByCountry$ = this.countryControl.valueChanges.pipe(
    switchMap((country) => this.searchDataService.getTrendingHRArticles(country)),
    tap(() => this.clearFilters(false)),
    shareReplay()
  );

  articleList$ = combineLatest([
    this.sortControl.valueChanges.pipe(startWith(this.consts.LABEL_SORT_MOST)),
    this.ratingControl.valueChanges.pipe(startWith(<string>null)),
    this.kbControl.valueChanges.pipe(startWith(<string>null)),
    this.categoryControl.valueChanges.pipe(startWith(<string>null)),
    this.countryControl.valueChanges
  ]).pipe(
    distinctUntilChanged(),
    tap(() => {
      this.isLoading = true;
      this.disableFilters();
    }),
    debounceTime(this.consts.DEBOUNCE_TIME),
    switchMap(([sort, rating, kb, category, country]) =>
      this.searchDataService
        .getTrendingHRArticles(country)
        .pipe(map((results) => this.sortResults(sort, rating, kb, category, results)))
    ),
    tap(() => {
      this.isLoading = false;
      this.enableFilters();
    }),
    share()
  );

  categoryFilters$ = this.articleFiltersBase('kbcategory');
  ratingFilters$ = this.articleList$.pipe(
    map((articles) => [...new Set(articles.map((a) => Math.floor(a['rating'])).sort((a, b) => b - a))])
  );
  kbFilters$ = this.articleFiltersBase('kbknowledgebase');

  constructor(private searchDataService: SearchDataService, private peopleService: PeopleService) {}

  ngOnInit(): void {
    this.subs = new SubSink();

    // Add global to countries
    !this.countryList.includes(this.consts.GLOBAL_OPTION) && this.countryList.unshift(this.consts.GLOBAL_OPTION);

    // Category dropdown options
    this.subs.sink = this.categoryFilters$.subscribe((res) => {
      if (this.categoryOptions.length === 0) {
        this.categoryOptions = res;
      }
    });

    // Rating dropdown options
    this.subs.sink = this.ratingFilters$.subscribe((res) => {
      if (this.ratingOptions.length === 0) {
        this.ratingOptions = res;
      }
    });

    // Knowledge base (KB) dropdown options
    this.subs.sink = this.kbFilters$.subscribe((res) => {
      if (this.kbOptions.length === 0) {
        this.kbOptions = res;
      }
    });
  }

  ngOnDestroy(): void {
    this.subs.unsubscribe();
  }

  articleFiltersBase(property: string): Observable<string[]> {
    return this.articleList$.pipe(map((articles) => this.uniqueArticleFilters(articles, property)));
  }

  uniqueArticleFilters(articles: IArticle[], field: string): string[] {
    return articles.reduce((prev: string[], curr: IArticle) => {
      if (!prev.some((e) => e === curr[field])) {
        prev.push(curr[field]);
      }
      return prev.sort();
    }, []);
  }

  openLink(url: string): void {
    window.open(url, '_blank');
  }

  sortResults(sort: string, rating: number | string, kb: string, category: string, results: IArticle[]): IArticle[] {
    if (sort === this.consts.LABEL_SORT_NEW) {
      results = results.sort((a, b) => new Date(b.syscreatedon).getTime() - new Date(a.syscreatedon).getTime());
    } else if (sort === this.consts.LABEL_SORT_MOST) {
      results = results.sort((a, b) => b.sysviewcount - a.sysviewcount);
    } else {
      results = results.sort((a, b) => new Date(b.sysupdatedon).getTime() - new Date(a.sysupdatedon).getTime());
    }

    if (rating !== null) {
      results = results.filter((r) => Math.floor(r.rating) === (rating as number));
    }
    if (category) {
      results = results.filter((c) => c.kbcategory === category);
    }
    if (kb) {
      results = results.filter((c) => c.kbknowledgebase === kb);
    }

    return results;
  }

  clearFilters(emitEvent: boolean): void {
    this.sortControl.setValue(this.consts.LABEL_SORT_MOST, { emitEvent });
    this.sortControl.markAsPristine();
    this.ratingControl.setValue(null, { emitEvent });
    this.ratingControl.markAsPristine();
    this.kbControl.setValue(null, { emitEvent });
    this.kbControl.markAsPristine();
    this.categoryControl.setValue(null, { emitEvent });
    this.categoryControl.markAsPristine();

    if (emitEvent) {
      this.countryControl.setValue(this.defaultCountry);
      this.countryControl.markAsPristine();
    }
  }

  disableFilters(): void {
    this.sortControl.disable({ emitEvent: false });
    this.ratingControl.disable({ emitEvent: false });
    this.kbControl.disable({ emitEvent: false });
    this.countryControl.disable({ emitEvent: false });
    this.categoryControl.disable({ emitEvent: false });
  }

  enableFilters(): void {
    this.sortControl.enable({ emitEvent: false });
    this.ratingControl.enable({ emitEvent: false });
    this.kbControl.enable({ emitEvent: false });
    this.countryControl.enable({ emitEvent: false });
    this.categoryControl.enable({ emitEvent: false });
  }

  isFiltersDirty(): boolean {
    return (
      this.sortControl.dirty ||
      this.ratingControl.dirty ||
      this.kbControl.dirty ||
      this.categoryControl.dirty ||
      this.countryControl.dirty
    );
  }

  private checkCountryCode(code: string): string {
    return Object.keys(this.consts.COUNTRIES_ISO_LOOKUP).includes(code)
      ? (this.consts.COUNTRIES_ISO_LOOKUP[code] as string)
      : WorldCities.getCountry(code).name;
  }
}
